// To parse this data:
//
//   import { Convert, NkkResult } from "./file";
//
//   const nkkResult = Convert.toNkkResult(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

export interface NkkResult {
  id: string
  nkkEventId: string
  competitions: NkkResultCompetition[]
}

export interface NkkResultCompetition {
  id: string
  nkkEventId: string
  type: string
  activity: string
  billableAmount: number
  date: string
  level: string
  size: string
  name: string
  judge: NkkResultJudge
  resultList: NkkResultListItem[]
}

export interface NkkResultJudge {
  name: string
  id: string
}

export interface NkkResultListItem {
  id: string
  dog?: NkkResultDog
  handlerName?: string
  team?: NkkResultTeam
  sum: number
  time: number
  halts: number
  dns: boolean
  placement: number
  faults: number
  dnf: boolean
  timeFault: number
  napp: boolean
  cert: boolean
  championat: boolean
  caciag: boolean
  rcaciag: boolean
}

export interface NkkResultDog {
  name: string
  huId: string
}
export interface NkkResultTeamMember {
  handlerName: string
  huId: string
}
export interface NkkResultTeam {
  name: string
  clubId: number
  teamId: string
  members: NkkResultTeamMember[]
}

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
  public static toNkkResult(json: string): NkkResult {
    return cast(JSON.parse(json), r('NkkResult'))
  }

  public static nkkResultToJson(value: NkkResult): string {
    return JSON.stringify(uncast(value, r('NkkResult')), null, 2)
  }
}

function invalidValue(typ: any, val: any, key: any = ''): never {
  if (key) {
    throw Error(
      `Invalid value for key "${key}". Expected type ${JSON.stringify(
        typ
      )} but got ${JSON.stringify(val)}`
    )
  }
  throw Error(
    `Invalid value ${JSON.stringify(val)} for type ${JSON.stringify(typ)}`
  )
}

function jsonToJSProps(typ: any): any {
  if (typ.jsonToJS === undefined) {
    const map: any = {}
    typ.props.forEach((p: any) => (map[p.json] = { key: p.js, typ: p.typ }))
    typ.jsonToJS = map
  }
  return typ.jsonToJS
}

function jsToJSONProps(typ: any): any {
  if (typ.jsToJSON === undefined) {
    const map: any = {}
    typ.props.forEach((p: any) => (map[p.js] = { key: p.json, typ: p.typ }))
    typ.jsToJSON = map
  }
  return typ.jsToJSON
}

function transform(val: any, typ: any, getProps: any, key: any = ''): any {
  function transformPrimitive(typ: string, val: any): any {
    if (typeof typ === typeof val) return val
    return invalidValue(typ, val, key)
  }

  function transformUnion(typs: any[], val: any): any {
    // val must validate against one typ in typs
    const l = typs.length
    for (let i = 0; i < l; i++) {
      const typ = typs[i]
      try {
        return transform(val, typ, getProps)
      } catch (_) {}
    }
    return invalidValue(typs, val)
  }

  function transformEnum(cases: string[], val: any): any {
    if (cases.indexOf(val) !== -1) return val
    return invalidValue(cases, val)
  }

  function transformArray(typ: any, val: any): any {
    // val must be an array with no invalid elements
    if (!Array.isArray(val)) return invalidValue('array', val)
    return val.map((el) => transform(el, typ, getProps))
  }

  function transformDate(val: any): any {
    if (val === null) {
      return null
    }
    const d = new Date(val)
    if (isNaN(d.valueOf())) {
      return invalidValue('Date', val)
    }
    return d
  }

  function transformObject(
    props: { [k: string]: any },
    additional: any,
    val: any
  ): any {
    if (val === null || typeof val !== 'object' || Array.isArray(val)) {
      return invalidValue('object', val)
    }
    const result: any = {}
    Object.getOwnPropertyNames(props).forEach((key) => {
      const prop = props[key]
      const v = Object.prototype.hasOwnProperty.call(val, key)
        ? val[key]
        : undefined
      result[prop.key] = transform(v, prop.typ, getProps, prop.key)
    })
    Object.getOwnPropertyNames(val).forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(props, key)) {
        result[key] = transform(val[key], additional, getProps, key)
      }
    })
    return result
  }

  if (typ === 'any') return val
  if (typ === null) {
    if (val === null) return val
    return invalidValue(typ, val)
  }
  if (typ === false) return invalidValue(typ, val)
  while (typeof typ === 'object' && typ.ref !== undefined) {
    typ = typeMap[typ.ref]
  }
  if (Array.isArray(typ)) return transformEnum(typ, val)
  if (typeof typ === 'object') {
    return typ.hasOwnProperty('unionMembers')
      ? transformUnion(typ.unionMembers, val)
      : typ.hasOwnProperty('arrayItems')
      ? transformArray(typ.arrayItems, val)
      : typ.hasOwnProperty('props')
      ? transformObject(getProps(typ), typ.additional, val)
      : invalidValue(typ, val)
  }
  // Numbers can be parsed by Date but shouldn't be.
  if (typ === Date && typeof val !== 'number') return transformDate(val)
  return transformPrimitive(typ, val)
}

function cast<T>(val: any, typ: any): T {
  return transform(val, typ, jsonToJSProps)
}

function uncast<T>(val: T, typ: any): any {
  return transform(val, typ, jsToJSONProps)
}

function a(typ: any) {
  return { arrayItems: typ }
}

function u(...typs: any[]) {
  return { unionMembers: typs }
}

function o(props: any[], additional: any) {
  return { props, additional }
}

function m(additional: any) {
  return { props: [], additional }
}

function r(name: string) {
  return { ref: name }
}

const typeMap: any = {
  NkkResult: o(
    [
      { json: 'id', js: 'id', typ: '' },
      { json: 'nkkEventId', js: 'nkkEventId', typ: '' },
      { json: 'competitions', js: 'competitions', typ: a(r('Competition')) },
    ],
    false
  ),
  Competition: o(
    [
      { json: 'id', js: 'id', typ: '' },
      { json: 'nkkEventId', js: 'nkkEventId', typ: '' },
      { json: 'type', js: 'type', typ: '' },
      { json: 'activity', js: 'activity', typ: '' },
      { json: 'billableAmount', js: 'billableAmount', typ: 0 },
      { json: 'date', js: 'date', typ: Date },
      { json: 'level', js: 'level', typ: '' },
      { json: 'size', js: 'size', typ: '' },
      { json: 'name', js: 'name', typ: '' },
      { json: 'judge', js: 'judge', typ: r('Judge') },
      { json: 'resultList', js: 'resultList', typ: a(r('ResultList')) },
    ],
    false
  ),
  Judge: o(
    [
      { json: 'name', js: 'name', typ: '' },
      { json: 'id', js: 'id', typ: '' },
    ],
    false
  ),
  ResultList: o(
    [
      { json: 'id', js: 'id', typ: '' },
      { json: 'dog', js: 'dog', typ: r('Dog') },
      { json: 'handlerName', js: 'handlerName', typ: '' },
      { json: 'sum', js: 'sum', typ: 0 },
      { json: 'time', js: 'time', typ: 3.14 },
      { json: 'halts', js: 'halts', typ: 0 },
      { json: 'dns', js: 'dns', typ: true },
      { json: 'placement', js: 'placement', typ: 0 },
      { json: 'faults', js: 'faults', typ: 0 },
      { json: 'dnf', js: 'dnf', typ: true },
      { json: 'timeFault', js: 'timeFault', typ: 0 },
      { json: 'napp', js: 'napp', typ: true },
      { json: 'cert', js: 'cert', typ: true },
      { json: 'championat', js: 'championat', typ: true },
      { json: 'caciag', js: 'caciag', typ: true },
      { json: 'rcaciag', js: 'rcaciag', typ: true },
    ],
    false
  ),
  Dog: o(
    [
      { json: 'name', js: 'name', typ: '' },
      { json: 'huId', js: 'huId', typ: '' },
    ],
    false
  ),
}
